import { isRetryable, requiresReauth } from '@steado/admin-contract/errors';

import type { ErrorDetail } from '@steado/admin-contract/envelope';

/**
 * Every failure the console can see, in one type.
 *
 * Callers branch on `code`, never on `message` — messages are written for
 * people and get reworded. `requestId` matches the server log line exactly and
 * is surfaced in the UI so a bug report is a one-minute investigation.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | null;
  readonly details: readonly ErrorDetail[];

  constructor(init: {
    status: number;
    code: string;
    message: string;
    requestId?: string | null;
    details?: readonly ErrorDetail[];
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.requestId = init.requestId ?? null;
    this.details = init.details ?? [];
  }

  get isRetryable(): boolean {
    return isRetryable(this.code);
  }

  get requiresReauth(): boolean {
    return requiresReauth(this.code);
  }

  /** Field-level messages, for rendering next to the inputs that caused them. */
  fieldErrors(): Record<string, string> {
    const result: Record<string, string> = {};
    for (const detail of this.details) {
      result[detail.path] ??= detail.message;
    }
    return result;
  }
}

/**
 * The network never answered — DNS failure, connection refused, CORS. Modelled
 * as an ApiError so callers have one thing to catch, and marked retryable
 * because the request may well have never left the machine.
 */
export function networkError(cause: unknown): ApiError {
  return new ApiError({
    status: 0,
    code: 'UPSTREAM_UNAVAILABLE',
    message:
      cause instanceof Error && cause.message !== ''
        ? `Could not reach the API: ${cause.message}`
        : 'Could not reach the API.',
  });
}
